// hooks/useResizableColumns.ts
// UI Tier 3.3a: draggable dividers between the three desktop columns
// (Degree Requirements | Academic Plan | AI Assistant).
//
// Widths are stored as fr fractions and persisted to localStorage. Each
// divider is a real separator: pointer-drag to resize, arrow keys to nudge
// (WAI-ARIA window-splitter pattern), double-click to reset to defaults.

import { useCallback, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';

const STORAGE_KEY = 'planner_column_fractions_v1';
const DEFAULT_FRACTIONS: [number, number, number] = [1, 1, 1.3];
/** No column may shrink below this share of the default total (~17%). */
const MIN_FRACTION = 0.55;
/** Keyboard nudge per arrow press, in fr units. */
const KEY_STEP = 0.06;

export interface SeparatorProps {
  role: 'separator';
  'aria-orientation': 'vertical';
  'aria-label': string;
  'aria-valuenow': number;
  'aria-valuemin': number;
  'aria-valuemax': number;
  tabIndex: 0;
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => void;
  onDoubleClick: () => void;
}

export interface UseResizableColumnsReturn {
  /** style for the grid container on desktop */
  gridStyle: CSSProperties;
  /** props for divider i (0 = between col 1 and 2, 1 = between col 2 and 3) */
  getSeparatorProps: (i: 0 | 1) => SeparatorProps;
  /** true while a divider is being dragged (used to disable text selection) */
  isResizing: boolean;
}

const loadFractions = (): [number, number, number] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FRACTIONS;
    const parsed = JSON.parse(raw) as unknown;
    if (
      Array.isArray(parsed) &&
      parsed.length === 3 &&
      parsed.every((f) => typeof f === 'number' && f >= MIN_FRACTION && f <= 5)
    ) {
      return parsed as [number, number, number];
    }
  } catch {
    /* corrupted storage — fall through to defaults */
  }
  return DEFAULT_FRACTIONS;
};

/**
 * @param columnCount 3 = normal desktop (audit | planner | chat); 2 = chat
 *   column collapsed (UX-R1 3.1 — assistant disabled → bottom-right pill).
 *   Fractions persist 3-wide either way, so re-enabling the assistant
 *   restores the user's saved widths.
 */
export const useResizableColumns = (columnCount: 2 | 3 = 3): UseResizableColumnsReturn => {
  const [fractions, setFractions] = useState<[number, number, number]>(loadFractions);
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef<{
    divider: 0 | 1;
    startX: number;
    startFractions: [number, number, number];
    frPerPx: number;
  } | null>(null);

  const persist = useCallback((next: [number, number, number]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* private mode — resize still works for the session */
    }
  }, []);

  const applyDelta = useCallback(
    (divider: 0 | 1, deltaFr: number, base: [number, number, number]): [number, number, number] => {
      const next: [number, number, number] = [...base] as [number, number, number];
      const left = divider;
      const right = (divider + 1) as 1 | 2;
      // clamp so neither neighbor goes below the minimum
      const maxGrow = base[right] - MIN_FRACTION;
      const maxShrink = base[left] - MIN_FRACTION;
      const clamped = Math.max(-maxShrink, Math.min(maxGrow, deltaFr));
      next[left] = base[left] + clamped;
      next[right] = base[right] - clamped;
      return next;
    },
    []
  );

  const handlePointerDown = useCallback(
    (divider: 0 | 1) => (e: ReactPointerEvent<HTMLElement>) => {
      e.preventDefault();
      const grid = (e.currentTarget as HTMLElement).parentElement;
      const gridWidth = grid?.getBoundingClientRect().width ?? window.innerWidth;
      const totalFr =
        columnCount === 2
          ? fractions[0] + fractions[1]
          : fractions[0] + fractions[1] + fractions[2];
      dragRef.current = {
        divider,
        startX: e.clientX,
        startFractions: fractions,
        frPerPx: totalFr / gridWidth,
      };
      setIsResizing(true);

      const onMove = (ev: PointerEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        const deltaFr = (ev.clientX - drag.startX) * drag.frPerPx;
        setFractions(applyDelta(drag.divider, deltaFr, drag.startFractions));
      };
      const onUp = (ev: PointerEvent) => {
        const drag = dragRef.current;
        if (drag) {
          const deltaFr = (ev.clientX - drag.startX) * drag.frPerPx;
          const next = applyDelta(drag.divider, deltaFr, drag.startFractions);
          setFractions(next);
          persist(next);
        }
        dragRef.current = null;
        setIsResizing(false);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [fractions, applyDelta, persist, columnCount]
  );

  const handleKeyDown = useCallback(
    (divider: 0 | 1) => (e: ReactKeyboardEvent<HTMLElement>) => {
      let deltaFr = 0;
      if (e.key === 'ArrowLeft') deltaFr = -KEY_STEP;
      else if (e.key === 'ArrowRight') deltaFr = KEY_STEP;
      else if (e.key === 'Home') deltaFr = -Infinity;
      else if (e.key === 'End') deltaFr = Infinity;
      else return;
      e.preventDefault();
      setFractions((prev) => {
        const bounded = Number.isFinite(deltaFr)
          ? deltaFr
          : deltaFr < 0
            ? -(prev[divider] - MIN_FRACTION)
            : prev[(divider + 1) as 1 | 2] - MIN_FRACTION;
        const next = applyDelta(divider, bounded, prev);
        persist(next);
        return next;
      });
    },
    [applyDelta, persist]
  );

  const handleReset = useCallback(() => {
    setFractions(DEFAULT_FRACTIONS);
    persist(DEFAULT_FRACTIONS);
  }, [persist]);

  const getSeparatorProps = useCallback(
    (i: 0 | 1): SeparatorProps => {
      const total =
        columnCount === 2
          ? fractions[0] + fractions[1]
          : fractions[0] + fractions[1] + fractions[2];
      const leftShare = i === 0 ? fractions[0] : fractions[0] + fractions[1];
      return {
        role: 'separator',
        'aria-orientation': 'vertical',
        'aria-label':
          i === 0
            ? 'Resize degree requirements and academic plan columns'
            : 'Resize academic plan and assistant columns',
        'aria-valuenow': Math.round((leftShare / total) * 100),
        'aria-valuemin': 10,
        'aria-valuemax': 90,
        tabIndex: 0,
        onPointerDown: handlePointerDown(i),
        onKeyDown: handleKeyDown(i),
        onDoubleClick: handleReset,
      };
    },
    [fractions, handlePointerDown, handleKeyDown, handleReset, columnCount]
  );

  return {
    gridStyle: {
      gridTemplateColumns:
        columnCount === 2
          ? `${fractions[0]}fr auto ${fractions[1]}fr`
          : `${fractions[0]}fr auto ${fractions[1]}fr auto ${fractions[2]}fr`,
    },
    getSeparatorProps,
    isResizing,
  };
};
